import EmptyState from "../../components/shared/EmptyState";
import ErrorState from "../../components/shared/ErrorState";
import LoadingState from "../../components/shared/LoadingState";
import ScheduleDayTable from "../../components/schedule/ScheduleDayTable";
import ScheduleSectionCard from "../../components/schedule/ScheduleSectionCard";
import { SchedulePosition } from "../../interfaces/ISchedules";
import { usePublishedSchedules } from "../../hooks/usePublishedSchedules";
import { useNavigationHelper } from "../../hooks/useNavigationHelper";
import { useCompetitionsContext } from "../../contexts/CompetitionsContext";
import { Container, Typography } from "@mui/material";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

/** Widok jednego dnia harmonogramu — tabela pozycji (read-only). */
export default function ScheduleDayPage() {
  const { competitionId, scheduleId } = useParams<{ competitionId: string; scheduleId: string }>();
  const { schedules, loading, error, refreshSchedules } = usePublishedSchedules(competitionId);
  const { goToSchedule, goToScheduleList, goToCompetitions, goToSchedulePosition } = useNavigationHelper();
  const { competitions } = useCompetitionsContext();

  const competitionName = useMemo(() => {
    if (!competitionId) {
      return undefined;
    }
    const competition = competitions.find((item) => item.id === competitionId);
    return competition?.fullName || competition?.name;
  }, [competitionId, competitions]);

  const schedule = schedules.find((item) => item.id === scheduleId);

  if (!competitionId || !scheduleId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <EmptyState message="Nieprawidłowy adres harmonogramu." />
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LoadingState message="Ładowanie harmonogramu..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ErrorState message={error} onRetry={refreshSchedules} />
      </Container>
    );
  }

  if (!schedule) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <ScheduleSectionCard
          title="Harmonogram"
          subtitle="Nie znaleziono dnia"
          tertiary={competitionName}
          onBack={() => goToSchedule(competitionId)}
        >
          <Typography variant="body2" color="text.secondary">
            Ten dzień harmonogramu nie jest dostępny lub nie jest już opublikowany.
          </Typography>
        </ScheduleSectionCard>
      </Container>
    );
  }

  const handleBack = () => {
    if (schedules.length > 1) {
      goToScheduleList(competitionId);
    } else {
      goToCompetitions();
    }
  };

  const dayTitle = schedule.dayLabel || `Dzień ${schedule.day}`;

  const handlePositionClick = (position: SchedulePosition) => {
    if (!competitionId) {
      return;
    }
    goToSchedulePosition(competitionId, schedule.id, position.id);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <ScheduleSectionCard title="Harmonogram" subtitle={dayTitle} tertiary={competitionName} onBack={handleBack}>
        <ScheduleDayTable schedule={schedule} onPositionClick={handlePositionClick} />
      </ScheduleSectionCard>
    </Container>
  );
}
